/**
 * ResearchSignalChip — surfaces the OpenAlex-derived research band
 * (world-class · strong · regular · emerging) with hover-details on
 * publications, h-index and top research topics. Silent when a uni
 * hasn't yet been enriched (Wave 1 covers the top 40 UK+US flagships;
 * Waves 2-N will extend coverage).
 *
 * Added 2026-02-05 as part of the OpenAlex + Wikidata enrichment wave.
 */
import { useState } from "react";
import { FlaskConical, X, ExternalLink } from "lucide-react";

const BAND_STYLE = {
  "world-class": {
    label: "World-class research",
    ring: "border-emerald-400 bg-emerald-50 text-emerald-800",
  },
  strong: {
    label: "Strong research",
    ring: "border-sky-400 bg-sky-50 text-sky-800",
  },
  regular: {
    label: "Research-active",
    ring: "border-amber-400 bg-amber-50 text-amber-900",
  },
  emerging: {
    label: "Emerging research",
    ring: "border-stone-300 bg-stone-50 text-stone-700",
  },
};

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export default function ResearchSignalChip({ enrichment, universityName }) {
  const [open, setOpen] = useState(false);
  const r = enrichment?.research;
  if (!r || !r.signal_band || r.signal_band === "unknown") return null;
  const style = BAND_STYLE[r.signal_band] || BAND_STYLE.regular;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="research-signal-chip"
        title={`OpenAlex research band · click for details`}
        className={
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-underground-sc tracking-wider hover:shadow-sm transition " +
          style.ring
        }
      >
        <FlaskConical className="h-3 w-3" aria-hidden />
        {style.label}
      </button>

      {open && (
        <div
          data-testid="research-signal-modal"
          role="dialog"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-stone-900/40 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-t-xl sm:rounded-xl max-w-lg w-full p-6 shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-700"
              data-testid="research-signal-modal-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
              Research signal
            </div>
            <h3 className="mt-1 font-editorial text-2xl text-[#002147]">
              {universityName || "This university"} — {style.label.toLowerCase()}
            </h3>
            <p className="mt-1 text-[13px] text-stone-500">
              Based on all peer-reviewed publications indexed by OpenAlex.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <Stat label="Publications" value={fmtNum(r.works_count)} />
              <Stat label="Citations" value={fmtNum(r.cited_by_count)} />
              <Stat label="h-index" value={r.h_index ?? "—"} />
              <Stat
                label="Avg citations / paper (2y)"
                value={r.two_year_mean_citedness != null
                  ? r.two_year_mean_citedness.toFixed(2)
                  : "—"}
              />
            </div>

            {r.top_topics?.length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500 mb-1.5">
                  Strongest research topics
                </div>
                <ul className="space-y-1">
                  {r.top_topics.map((t, i) => (
                    <li
                      key={i}
                      data-testid={`research-signal-topic-${i}`}
                      className="text-[13px] text-stone-700 flex items-baseline gap-2"
                    >
                      <span className="text-stone-400 tabular-nums text-[11px] w-8">
                        {i + 1}.
                      </span>
                      <span className="flex-1">{t.name}</span>
                      <span className="text-stone-400 tabular-nums text-[11px]">
                        {fmtNum(t.score)} papers
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-stone-200 text-[11px] text-stone-500 flex items-center justify-between gap-3">
              <span>
                Data: OpenAlex (CC0). Fetched{" "}
                {enrichment.provenance?.fetched_at
                  ? new Date(enrichment.provenance.fetched_at).toISOString().slice(0, 10)
                  : "recently"}
                .
              </span>
              {enrichment.openalex_id && (
                <a
                  href={enrichment.openalex_id}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
                  data-testid="research-signal-openalex-link"
                >
                  OpenAlex <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-stone-200 bg-stone-50 p-3">
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
        {label}
      </div>
      <div className="mt-0.5 font-editorial text-xl text-[#002147] tabular-nums">
        {value}
      </div>
    </div>
  );
}
